import { useCallback, useEffect, useState, type ComponentType } from 'react'
import { OficinaTabletop } from './apresentacao/oficinas/tabletop/OficinaTabletop'
import { ProjecaoMesa } from './apresentacao/oficinas/tabletop/ProjecaoMesa'
import { OficinaBleve } from './apresentacao/oficinas/bleve/OficinaBleve'
import { OficinaPluma } from './apresentacao/oficinas/pluma/OficinaPluma'
import { OficinaTransbordo } from './apresentacao/oficinas/transbordo/OficinaTransbordo'
import { OficinaIdentificacao } from './apresentacao/oficinas/identificacao/OficinaIdentificacao'
import type { Tema } from './apresentacao/workspace/BarraAplicacao'
import type { ContextoCasca } from './apresentacao/workspace/CascaOficina'
import type { IdModulo } from './apresentacao/workspace/modulos'
import { useModoLayout } from './apresentacao/workspace/useModoLayout'
import { useControleCamera } from './visualizacao3d/camera/comandosCamera'

const CHAVE_TEMA = 'qbrn-lab-tema'

/**
 * Uma oficina por módulo. Cada uma é dona do seu estado e monta a própria
 * tela pela `CascaOficina`; `App` só escolhe qual está aberta e guarda o que é
 * comum a todas (tema, layout, câmera).
 */
const OFICINAS: Record<IdModulo, ComponentType<{ contexto: ContextoCasca }>> = {
  caminhao: OficinaIdentificacao,
  bleve: OficinaBleve,
  pluma: OficinaPluma,
  transbordo: OficinaTransbordo,
  tabletop: OficinaTabletop,
}

function temaSalvo(): Tema {
  try {
    return window.localStorage.getItem(CHAVE_TEMA) === 'claro' ? 'claro' : 'escuro'
  } catch {
    return 'escuro'
  }
}

export default function App() {
  const [tema, setTema] = useState<Tema>(temaSalvo)
  const [idModulo, setIdModulo] = useState<IdModulo>('caminhao')
  const modoLayout = useModoLayout()
  const camera = useControleCamera()

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_TEMA, tema)
    } catch {
      // armazenamento indisponível (aba privativa): o tema só não persiste
    }
  }, [tema])

  const aoTrocarModulo = useCallback(
    (id: IdModulo) => {
      setIdModulo(id)
      camera.executar('restaurar')
    },
    [camera],
  )

  const contexto: ContextoCasca = {
    idModulo,
    aoTrocarModulo,
    tema,
    aoAlternarTema: () => setTema((atual) => (atual === 'claro' ? 'escuro' : 'claro')),
    modoLayout,
    camera,
  }

  if (window.location.hash === '#projecao-tabletop') return <ProjecaoMesa />

  const Oficina = OFICINAS[idModulo]
  // `key`: trocar de módulo monta a oficina do zero, sem herdar seleção e grupo aberto da anterior
  return <Oficina key={idModulo} contexto={contexto} />
}
